import { useCallback, useState } from 'react';
import { type GestureResponderEvent, Platform } from 'react-native';

interface HoverProps {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}

interface UseHoverStateReturn {
  isHovered: boolean;
  hoverProps: HoverProps;
  setIsHovered: (value: boolean) => void;
}

export function useHoverState(initialValue = false): UseHoverStateReturn {
  const [isHovered, setIsHovered] = useState(initialValue);

  const onHoverIn = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onHoverOut = useCallback(() => {
    setIsHovered(false);
  }, []);

  const hoverProps: HoverProps = Platform.select({
    web: {
      onMouseEnter: onHoverIn,
      onMouseLeave: onHoverOut,
    },
    default: {
      onHoverIn,
      onHoverOut,
    },
  }) as HoverProps;

  return {
    isHovered,
    hoverProps,
    setIsHovered,
  };
}

interface UsePressHoverReturn {
  isPressed: boolean;
  isHovered: boolean;
  pressProps: {
    onPressIn: (event: GestureResponderEvent) => void;
    onPressOut: (event: GestureResponderEvent) => void;
  };
  hoverProps: HoverProps;
}

export function usePressHoverState(): UsePressHoverReturn {
  const [isPressed, setIsPressed] = useState(false);
  const { isHovered, hoverProps } = useHoverState();

  const onPressIn = useCallback(() => {
    setIsPressed(true);
  }, []);

  const onPressOut = useCallback(() => {
    setIsPressed(false);
  }, []);

  return {
    isPressed,
    isHovered,
    pressProps: {
      onPressIn,
      onPressOut,
    },
    hoverProps,
  };
}
